import { TableCell, TableRow } from '@atlas/ui-components'
import { Badge, Button, Checkbox, Chip, Textarea } from '@atlas/ui-primitives'
import { Pencil } from 'lucide-react'
import { MESSAGE_TYPE_LABEL } from '@/domain/message.types'
import type { MatrixCell, MatrixRow } from './matrix-model'
import {
	BulkSelectState,
	bulkSelectState,
	isCellInFlight,
	isCellSelectable,
	MatrixJobState,
	MatrixProvenance,
	selectableKeysInRow,
} from './matrix-model'
import { buildRequestKey } from './request-key'

const PROVENANCE_CHIP: Record<MatrixProvenance, { variant: 'success' | 'primary'; label: string }> = {
	[MatrixProvenance.MANUAL]: { variant: 'success', label: 'manual' },
	[MatrixProvenance.AI]: { variant: 'primary', label: 'AI' },
}

/** Only the states worth interrupting the provenance chip for; a finished job just reads as `AI`. */
const JOB_BADGE: Partial<Record<MatrixJobState, { variant: 'muted' | 'primary' | 'danger'; label: string }>> = {
	[MatrixJobState.PENDING]: { variant: 'muted', label: 'Pending' },
	[MatrixJobState.TRANSLATING]: { variant: 'primary', label: 'Translating…' },
	[MatrixJobState.FAILED]: { variant: 'danger', label: 'Failed' },
	[MatrixJobState.STUCK]: { variant: 'danger', label: 'Stuck' },
}

/** The row-editing surface, owned by the container hook so the matrix itself stays prop-driven. */
export interface MatrixEditController {
	editingRowId: string | null
	draft: Record<string, string>
	startEdit: (row: MatrixRow) => void
	cancelEdit: () => void
	setDraftCell: (locale: string, content: string) => void
	saveEdit: (row: MatrixRow) => void | Promise<void>
	isSaving: boolean
	saveError?: string | null
}

/** One (message, locale) cell: its text, where that text came from, and its tick box. */
function MatrixCellView({
	cell,
	rowTitle,
	isRowLoading,
	isSelected,
	canSelect,
	onToggle,
}: {
	cell: MatrixCell
	rowTitle: string
	/** The group's variants are still in flight, so an empty cell here means "unknown", not "no copy". */
	isRowLoading: boolean
	isSelected: boolean
	canSelect: boolean
	onToggle: () => void
}) {
	const chip = cell.provenance ? PROVENANCE_CHIP[cell.provenance] : null
	const job = JOB_BADGE[cell.jobState]
	const isUnread = isRowLoading && !cell.message
	return (
		<div className="flex h-full min-h-24 flex-col gap-2 rounded-ds-md border border-ds-border bg-ds-surface p-3">
			<p className={cell.message ? 'text-sm text-ds-text' : 'text-sm text-ds-text-subtle'}>
				{cell.message?.content || '—'}
			</p>
			<div className="mt-auto flex items-center justify-between gap-2">
				{/* Always mounted so the FIRST status announced for this cell also
				    reaches assistive tech, matching the detail page's variant rows. */}
				<span aria-live="polite" className="contents">
					{job ? (
						<Badge variant={job.variant} size="sm">
							{job.label}
						</Badge>
					) : chip ? (
						<Chip variant={chip.variant} size="sm">
							{chip.label}
						</Chip>
					) : (
						<span className="font-mono text-ds-3xs uppercase tracking-wide text-ds-text-subtle">
							{isUnread ? 'loading…' : 'no copy'}
						</span>
					)}
				</span>
				{canSelect && isCellSelectable(cell, isRowLoading) ? (
					<Checkbox
						size="sm"
						isSelected={isSelected}
						onChange={onToggle}
						aria-label={`Select ${cell.locale} for ${rowTitle}`}
					/>
				) : null}
			</div>
		</div>
	)
}

/**
 * The same cell in edit mode: a textarea over the current text, or over nothing
 * for a locale with no row yet. A cell with a running job is read-only - the job
 * writes to it when it finishes, so whichever of the two landed last would win
 * silently.
 */
function MatrixCellEditor({
	cell,
	value,
	onChange,
}: {
	cell: MatrixCell
	value: string
	onChange: (v: string) => void
}) {
	const inFlight = isCellInFlight(cell)
	return (
		<Textarea
			size="sm"
			value={value}
			readOnly={inFlight}
			onChange={(e) => onChange(e.target.value)}
			placeholder={cell.message ? undefined : `Write the ${cell.locale} copy…`}
			aria-label={
				inFlight
					? `${cell.locale} copy for ${cell.message?.title ?? 'this message'} - being translated, read-only`
					: `${cell.locale} copy for ${cell.message?.title ?? 'this message'}`
			}
		/>
	)
}

/**
 * Edit / Save / Cancel for one row. Editing is one row at a time, so every other
 * row's button is disabled meanwhile - and so is a row whose group has not
 * arrived, whose every locale still reads empty: typing into one would create a
 * second row for a locale that already has one.
 */
function RowEditActions({ row, edit }: { row: MatrixRow; edit: MatrixEditController }) {
	if (edit.editingRowId === row.sourceMessageId)
		return (
			<div className="flex flex-wrap gap-2">
				<Button
					size="sm"
					className="rounded-ds-full"
					isDisabled={edit.isSaving}
					onPress={() => void edit.saveEdit(row)}
				>
					{edit.isSaving ? 'Saving…' : 'Save'}
				</Button>
				<Button variant="secondary" size="sm" className="rounded-ds-full" onPress={edit.cancelEdit}>
					Cancel
				</Button>
			</div>
		)
	return (
		<Button
			variant="secondary"
			size="sm"
			className="w-fit rounded-ds-full"
			isDisabled={edit.editingRowId !== null || row.isLoading}
			onPress={() => edit.startEdit(row)}
		>
			<Pencil className="mr-1 h-3 w-3" aria-hidden="true" /> Edit
		</Button>
	)
}

/**
 * The message identity column: its whole-row tick, title, type, placement when
 * the copy has one, and the row's edit actions. The tick is hidden while the row
 * is being edited, matching the cells, which are textareas then.
 */
function MatrixRowLabel({
	row,
	edit,
	rowSelection,
	canSelect,
	canEdit,
	onToggleRow,
}: {
	row: MatrixRow
	edit: MatrixEditController
	rowSelection: BulkSelectState
	canSelect: boolean
	canEdit: boolean
	onToggleRow: () => void
}) {
	const isEditing = edit.editingRowId === row.sourceMessageId
	return (
		<div className="flex min-w-48 flex-col gap-2">
			<div className="flex items-start gap-2">
				{canSelect && !isEditing && selectableKeysInRow(row).length > 0 ? (
					<Checkbox
						size="sm"
						className="mt-0.5 shrink-0"
						isSelected={rowSelection === BulkSelectState.ALL}
						isIndeterminate={rowSelection === BulkSelectState.SOME}
						onChange={onToggleRow}
						aria-label={`Select every locale for ${row.title}`}
					/>
				) : null}
				<p className="font-medium text-ds-text">{row.title}</p>
			</div>
			<div className="flex flex-wrap items-center gap-1.5">
				<Chip variant="muted" size="sm">
					{MESSAGE_TYPE_LABEL[row.messageType]}
				</Chip>
				{row.position ? (
					<Chip variant="muted" size="sm">
						{row.position}
					</Chip>
				) : null}
			</div>
			{canEdit ? <RowEditActions row={row} edit={edit} /> : null}
		</div>
	)
}

/** One translation group as a table row: its identity, then one cell per locale column. */
export function MatrixRowView({
	row,
	selection,
	canSelect,
	canEdit,
	onToggleCell,
	onToggleRow,
	edit,
	hasTrailingColumn = false,
}: {
	row: MatrixRow
	selection: ReadonlySet<string>
	/** Whether this surface offers AI translation at all — the tick boxes exist only to feed it. */
	canSelect: boolean
	canEdit: boolean
	onToggleCell: (sourceMessageId: string, locale: string) => void
	onToggleRow: (row: MatrixRow) => void
	edit: MatrixEditController
	/** Set when the header carries an add-column plus, so the row runs the full width of the grid under it. */
	hasTrailingColumn?: boolean
}) {
	const isEditing = edit.editingRowId === row.sourceMessageId
	return (
		<TableRow className="align-top">
			<TableCell className="p-2">
				<MatrixRowLabel
					row={row}
					edit={edit}
					rowSelection={bulkSelectState(selectableKeysInRow(row), selection)}
					canSelect={canSelect}
					canEdit={canEdit}
					onToggleRow={() => onToggleRow(row)}
				/>
				{isEditing && edit.saveError ? (
					<p role="alert" className="mt-2 max-w-48 text-ds-sm text-ds-danger">
						{edit.saveError}
					</p>
				) : null}
			</TableCell>
			{row.cells.map((cell) => (
				<TableCell key={cell.locale} className="min-w-56 p-2">
					{isEditing ? (
						<MatrixCellEditor
							cell={cell}
							value={edit.draft[cell.locale] ?? ''}
							onChange={(v) => edit.setDraftCell(cell.locale, v)}
						/>
					) : (
						<MatrixCellView
							cell={cell}
							rowTitle={row.title}
							isRowLoading={row.isLoading}
							isSelected={selection.has(buildRequestKey(row.sourceMessageId, cell.locale))}
							canSelect={canSelect}
							onToggle={() => onToggleCell(row.sourceMessageId, cell.locale)}
						/>
					)}
				</TableCell>
			))}
			{hasTrailingColumn ? <TableCell className="p-2" /> : null}
		</TableRow>
	)
}
